import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { buildEmailAccountSchema, EmailAccountFormData } from '@/schemas/emailAccountSchema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Loader2, Server, Zap, Check } from 'lucide-react';
import { EmailAccount, EmailProvider } from '@/services/api/emailAccounts';
import { cn } from '@/lib/utils';

interface EmailAccountFormProps {
  account?: EmailAccount;
  onSubmit: (data: EmailAccountFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const EmailAccountForm = ({
  account,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: EmailAccountFormProps) => {
  const isEdit = !!account;
  const [imapOpen, setImapOpen] = useState(true);
  const [smtpOpen, setSmtpOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Schema depends on create vs edit (Hostinger token required only on create)
  const schema = useMemo(() => buildEmailAccountSchema(isEdit), [isEdit]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EmailAccountFormData>({
    resolver: zodResolver(schema),
    defaultValues: account
      ? {
          ...account,
          provider: account.provider || 'imap',
          hostinger_token: '',
          imap_server: account.imap_server || '',
          imap_port: account.imap_port || 993,
          imap_username: account.imap_username || '',
          imap_password: account.imap_password || '',
          imap_mailbox: account.imap_mailbox || 'INBOX',
          smtp_server: account.smtp_server || '',
          smtp_port: account.smtp_port || 587,
          smtp_username: account.smtp_username || '',
          smtp_password: account.smtp_password || '',
        }
      : {
          provider: 'imap',
          email_address: '',
          display_name: '',
          signature_html: '',
          hostinger_token: '',
          imap_server: '',
          imap_port: 993,
          imap_username: '',
          imap_password: '',
          imap_mailbox: 'INBOX',
          smtp_server: '',
          smtp_port: 587,
          smtp_username: '',
          smtp_password: '',
          use_ssl: true,
          trust_self_signed: false,
          max_sync_per_run: 200,
          sync_window_days: 30,
          sync_delete_email_from_server: false,
        },
  });

  const provider = watch('provider');
  const useSSL = watch('use_ssl');
  const trustSelfSigned = watch('trust_self_signed');
  const deleteFromServer = watch('sync_delete_email_from_server');

  const providerOptions: Array<{
    value: EmailProvider;
    label: string;
    description: string;
    icon: typeof Server;
  }> = [
    {
      value: 'imap',
      label: 'IMAP / SMTP',
      description: 'Any mail server with IMAP + SMTP credentials',
      icon: Server,
    },
    {
      value: 'hostinger',
      label: 'Hostinger Mail',
      description: 'Hostinger mailbox via API token — no server settings needed',
      icon: Zap,
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Provider Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Mail Provider</Label>
        <div className="grid grid-cols-2 gap-2">
          {providerOptions.map((option) => {
            const selected = provider === option.value;
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                disabled={isEdit}
                onClick={() => setValue('provider', option.value, { shouldValidate: true })}
                className={cn(
                  'relative flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-accent/50',
                  isEdit && 'opacity-70 cursor-not-allowed'
                )}
              >
                {selected && (
                  <Check className="absolute top-2 right-2 h-4 w-4 text-primary" />
                )}
                <span className="flex items-center gap-2 font-medium text-sm">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {option.label}
                </span>
                <span className="text-xs text-muted-foreground leading-snug">
                  {option.description}
                </span>
              </button>
            );
          })}
        </div>
        {isEdit && (
          <p className="text-xs text-muted-foreground">
            The provider can't be changed after the account is created.
          </p>
        )}
      </div>

      {/* Basic Information */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold">Basic Information</h3>

        <div className="space-y-1.5">
          <Label htmlFor="email_address" className="text-sm font-medium">Email Address</Label>
          <Input
            id="email_address"
            type="email"
            {...register('email_address')}
            placeholder="you@example.com"
            className="h-9"
          />
          {errors.email_address && (
            <p className="text-sm text-destructive">{errors.email_address.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="display_name" className="text-sm font-medium">Display Name</Label>
          <Input
            id="display_name"
            {...register('display_name')}
            placeholder="Your Name"
            className="h-9"
          />
          {errors.display_name && (
            <p className="text-sm text-destructive">{errors.display_name.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="signature_html" className="text-sm font-medium">Email Signature (optional)</Label>
          <textarea
            id="signature_html"
            {...register('signature_html')}
            className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Best regards,&#10;Your Name"
          />
        </div>
      </div>

      {/* Hostinger API Token */}
      {provider === 'hostinger' && (
        <div className="space-y-3 rounded-lg border border-border/60 p-3">
          <h3 className="text-base font-semibold">Hostinger API Access</h3>
          <div className="space-y-1.5">
            <Label htmlFor="hostinger_token" className="text-sm font-medium">
              Bearer Token {isEdit && <span className="text-muted-foreground font-normal">(leave blank to keep current)</span>}
            </Label>
            <Input
              id="hostinger_token"
              type="password"
              {...register('hostinger_token')}
              placeholder={isEdit ? '••••••••' : 'Paste your Hostinger API token'}
              className="h-9"
              autoComplete="off"
            />
            {errors.hostinger_token && (
              <p className="text-sm text-destructive">{errors.hostinger_token.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Entered once and stored securely on the server — it is never sent back to the browser.
              Generate one in your Hostinger panel under Mail API access.
            </p>
          </div>
        </div>
      )}

      {/* IMAP Settings — not used for Hostinger accounts */}
      {provider === 'imap' && (
        <Collapsible open={imapOpen} onOpenChange={setImapOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-1.5 hover:bg-accent rounded-md px-2">
            <h3 className="text-base font-semibold">IMAP Settings (Incoming Mail)</h3>
            <ChevronDown className={`h-4 w-4 transition-transform ${imapOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="imap_server" className="text-sm font-medium">IMAP Server</Label>
                <Input
                  id="imap_server"
                  {...register('imap_server')}
                  placeholder="imap.example.com"
                  className="h-9"
                />
                {errors.imap_server && (
                  <p className="text-sm text-destructive">{errors.imap_server.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="imap_port" className="text-sm font-medium">Port</Label>
                <Input
                  id="imap_port"
                  type="number"
                  {...register('imap_port', { valueAsNumber: true })}
                  className="h-9"
                />
                {errors.imap_port && (
                  <p className="text-sm text-destructive">{errors.imap_port.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="imap_username" className="text-sm font-medium">Username</Label>
              <Input
                id="imap_username"
                {...register('imap_username')}
                placeholder="username@example.com"
                className="h-9"
              />
              {errors.imap_username && (
                <p className="text-sm text-destructive">{errors.imap_username.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="imap_password" className="text-sm font-medium">Password</Label>
              <Input
                id="imap_password"
                type="password"
                {...register('imap_password')}
                placeholder="••••••••"
                className="h-9"
              />
              {errors.imap_password && (
                <p className="text-sm text-destructive">{errors.imap_password.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="imap_mailbox" className="text-sm font-medium">Mailbox</Label>
              <Input
                id="imap_mailbox"
                {...register('imap_mailbox')}
                placeholder="INBOX"
                className="h-9"
              />
              {errors.imap_mailbox && (
                <p className="text-sm text-destructive">{errors.imap_mailbox.message}</p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* SMTP Settings — not used for Hostinger accounts */}
      {provider === 'imap' && (
        <Collapsible open={smtpOpen} onOpenChange={setSmtpOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-1.5 hover:bg-accent rounded-md px-2">
            <h3 className="text-base font-semibold">SMTP Settings (Outgoing Mail)</h3>
            <ChevronDown className={`h-4 w-4 transition-transform ${smtpOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="smtp_server" className="text-sm font-medium">SMTP Server</Label>
                <Input
                  id="smtp_server"
                  {...register('smtp_server')}
                  placeholder="smtp.example.com"
                  className="h-9"
                />
                {errors.smtp_server && (
                  <p className="text-sm text-destructive">{errors.smtp_server.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="smtp_port" className="text-sm font-medium">Port</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  {...register('smtp_port', { valueAsNumber: true })}
                  className="h-9"
                />
                {errors.smtp_port && (
                  <p className="text-sm text-destructive">{errors.smtp_port.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="smtp_username" className="text-sm font-medium">Username</Label>
              <Input
                id="smtp_username"
                {...register('smtp_username')}
                placeholder="username@example.com"
                className="h-9"
              />
              {errors.smtp_username && (
                <p className="text-sm text-destructive">{errors.smtp_username.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="smtp_password" className="text-sm font-medium">Password</Label>
              <Input
                id="smtp_password"
                type="password"
                {...register('smtp_password')}
                placeholder="••••••••"
                className="h-9"
              />
              {errors.smtp_password && (
                <p className="text-sm text-destructive">{errors.smtp_password.message}</p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Advanced Settings — IMAP only */}
      {provider === 'imap' && (
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full py-1.5 hover:bg-accent rounded-md px-2">
            <h3 className="text-base font-semibold">Advanced Settings</h3>
            <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="use_ssl" className="text-sm font-medium">Use SSL/TLS</Label>
              <Switch
                id="use_ssl"
                checked={useSSL}
                onCheckedChange={(checked) => setValue('use_ssl', checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="trust_self_signed" className="text-sm font-medium">Trust Self-Signed Certificates</Label>
              <Switch
                id="trust_self_signed"
                checked={trustSelfSigned}
                onCheckedChange={(checked) => setValue('trust_self_signed', checked)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="max_sync_per_run" className="text-sm font-medium">Max Emails Per Sync</Label>
              <Input
                id="max_sync_per_run"
                type="number"
                {...register('max_sync_per_run', { valueAsNumber: true })}
                className="h-9"
              />
              {errors.max_sync_per_run && (
                <p className="text-sm text-destructive">{errors.max_sync_per_run.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sync_window_days" className="text-sm font-medium">Sync Window (days)</Label>
              <Input
                id="sync_window_days"
                type="number"
                {...register('sync_window_days', { valueAsNumber: true })}
                className="h-9"
              />
              {errors.sync_window_days && (
                <p className="text-sm text-destructive">{errors.sync_window_days.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="sync_delete_email_from_server" className="text-sm font-medium">Delete from Server After Sync</Label>
              <Switch
                id="sync_delete_email_from_server"
                checked={deleteFromServer}
                onCheckedChange={(checked) => setValue('sync_delete_email_from_server', checked)}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting} size="sm">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} size="sm">
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            'Save Account'
          )}
        </Button>
      </div>
    </form>
  );
};
